import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { Box, Typography } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";

import { Autocomplete } from "../../components/Form";
import Button from "../../components/Button";
import Confirm from "../../components/Confirm";
import { Model, SelectableWithIdObject } from "../../components/Form/types";
import { StyledSwitch } from "../../components/Base";

import { Season } from "../../api/courses";

interface RefresherRowProps {
  item: RefreshCourseRowModel;
  onChange: (updatedItem: RefreshCourseRowModel) => void;
  onRemove: () => void;
  allOptions: SelectableWithIdObject[];
  excludeOptions: SelectableWithIdObject[];
  seasons: Season[];
}

export class RefreshCourseRowModel extends SelectableWithIdObject implements Model {
  shortName?: string;
  isActive?: boolean;
  season?: string | null;
  getDisplayName!: () => string;

  constructor(data?: any) {
    super(data?.id);
    if (data && typeof data === "object") {
      this.shortName = data.shortName;
      this.isActive = Boolean(data.isActive);
      this.season = data.season ?? null;
      this.getDisplayName = () => (data.shortName ? `${data.shortName} (${data.id})` : "");
    }
  }

  validate(): boolean {
    return this && Object.keys(this).length > 0;
  }
}

const RefresherRow = ({ item, onChange, onRemove, allOptions, excludeOptions, seasons }: RefresherRowProps) => {
  const { t } = useTranslation();
  const [pendingSeasonValue, setPendingSeasonValue] = useState<string | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [inputValue, setInputValue] = useState<string>("");

  const getAvailableOptions = useCallback(() => {
    return allOptions.filter(
      (refresher) =>
        !excludeOptions.some((selected) => selected?.getId() === refresher.getId()) || refresher.getId() === item?.id,
    );
  }, [allOptions, excludeOptions, item]);

  const handleSeasonChange = (seasonValue: string | null) => {
    const updatedItem = new RefreshCourseRowModel({
      ...item,
      season: seasonValue,
    });
    onChange(updatedItem);
  };

  const checkAndConfirmNewSeason = (value: string) => {
    if (!value || !value.trim()) {
      handleSeasonChange(null);
      return;
    }

    const trimmedValue = value.trim();
    const isExistingSeason = seasons.some((s) => s.getId() === trimmedValue);

    if (!isExistingSeason) {
      setPendingSeasonValue(trimmedValue);
      setShowConfirmDialog(true);
    } else if (trimmedValue !== item.season) {
      handleSeasonChange(trimmedValue);
    }
  };

  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(2),
      })}
    >
      <Autocomplete
        sx={{ width: "100%" }}
        label={`${t("course.select_refresher")}*`}
        items={getAvailableOptions()}
        selected={item}
        onChange={(updatedItem) => {
          updatedItem.isActive = true;
          updatedItem.season = item?.season ?? null;
          onChange(updatedItem);
        }}
      />
      {seasons.length > 0 && (
        <Autocomplete
          sx={{ width: "100%" }}
          label={t("course.season")}
          items={seasons}
          selected={item.season ? seasons.find((season) => season.getId() === item.season) || item.season : null}
          onChange={(selectedSeason) => {
            if (!selectedSeason) {
              handleSeasonChange(null);
              setInputValue("");
              return;
            }

            // Only handle when selecting from dropdown (SelectableObject)
            if (typeof selectedSeason !== "string") {
              const seasonValue = String(selectedSeason.getId());
              handleSeasonChange(seasonValue);
              setInputValue(seasonValue);
            }
          }}
          onInputChange={(_event, value) => {
            setInputValue(value);
          }}
          onBlur={(_event) => {
            if (inputValue && inputValue.trim() !== item.season) {
              checkAndConfirmNewSeason(inputValue);
            }
          }}
          freeSolo
        />
      )}
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <Typography component="div">{t("is_active")}</Typography>
        <StyledSwitch
          title={t("course.refresher_active_toggle")}
          checked={item?.isActive ?? false}
          onChange={() => {
            if (item) {
              const updatedItem = new RefreshCourseRowModel({
                ...item,
                isActive: !item.isActive,
              });
              onChange(updatedItem);
            }
          }}
        />
      </Box>
      <Button title={t("course.remove_refresher_action")} icon={<ClearIcon />} onClick={onRemove} />
      {showConfirmDialog && pendingSeasonValue && (
        <Confirm
          title={t("course.confirm_new_season_title")}
          onConfirm={(confirmed) => {
            if (confirmed) {
              handleSeasonChange(pendingSeasonValue);
            }
            setShowConfirmDialog(false);
            setPendingSeasonValue(null);
          }}
        >
          {t("course.confirm_new_season_message", {
            season: pendingSeasonValue,
          })}
        </Confirm>
      )}
    </Box>
  );
};

export default RefresherRow;
